import React, { useEffect, useRef, useState } from 'react';
import './DatePicker.css';
import { DatePickerProps, DateRange } from './DatePicker.type';
import { Arrow } from '../../../icons/SvgIcons';

const DatePicker: React.FC<DatePickerProps> = ({
  onDateRangeChange,
  initialRange = { startDate: null, endDate: null },
  value,
  placeholder = 'Date Modified',
  className = '',
  singleDate = false,
  icon,
}) => {
  const [selectedRange, setSelectedRange] = useState<DateRange>(initialRange);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isOpen, setIsOpen] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value === null || value === undefined) {
      setSelectedRange({ startDate: null, endDate: null });
    } else {
      setSelectedRange(value);
    }
  }, [value]);

  const today = new Date();
  const nextMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    1
  );

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Helper functions
  const isSameDay = (date1: Date, date2: Date): boolean => {
    return date1.toDateString() === date2.toDateString();
  };

  const isToday = (date: Date): boolean => {
    return isSameDay(date, today);
  };

  const isInRange = (date: Date): boolean => {
    if (singleDate) return false;
    if (!selectedRange.startDate || !selectedRange.endDate) return false;
    return date >= selectedRange.startDate && date <= selectedRange.endDate;
  };

  const isStartDate = (date: Date): boolean => {
    return selectedRange.startDate
      ? isSameDay(date, selectedRange.startDate)
      : false;
  };

  const isEndDate = (date: Date): boolean => {
    if (singleDate) return false;
    return selectedRange.endDate
      ? isSameDay(date, selectedRange.endDate)
      : false;
  };

  const handleDateClick = (date: Date) => {
    if (singleDate) {
      const newRange = { startDate: date, endDate: null };
      setSelectedRange(newRange);
      onDateRangeChange?.(newRange);
      setIsOpen(false);
      return;
    }

    if (
      !selectedRange.startDate ||
      (selectedRange.startDate && selectedRange.endDate)
    ) {
      const newRange = { startDate: date, endDate: null };
      setSelectedRange(newRange);
      onDateRangeChange?.(newRange);
    } else if (selectedRange.startDate && !selectedRange.endDate) {
      const startDate = selectedRange.startDate;
      const endDate = date >= startDate ? date : startDate;
      const actualStartDate = date >= startDate ? startDate : date;

      const newRange = { startDate: actualStartDate, endDate };
      setSelectedRange(newRange);
      onDateRangeChange?.(newRange);
    }
  };

  const clearSelection = () => {
    const newRange = { startDate: null, endDate: null };
    setSelectedRange(newRange);
    onDateRangeChange?.(newRange);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(
      currentMonth.getMonth() + (direction === 'next' ? 1 : -1)
    );
    setCurrentMonth(newMonth);
  };

  const generateCalendarDays = (month: Date) => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, monthIndex, day));
    }

    return days;
  };

  const renderCalendarMonth = (month: Date, title: string) => {
    const days = generateCalendarDays(month);
    const monthName = month.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });

    return (
      <div className="flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <button
            id={`date-picker-prev-month-button-${title}`}
            data-testid={`date-picker-prev-month-button-${title}`}
            onClick={() => navigateMonth('prev')}
            className="nav-button"
          >
            {Arrow('left')}
          </button>

          <h3 className="month-title">{monthName}</h3>

          <button
            id={`date-picker-next-month-button-${title}`}
            data-testid={`date-picker-next-month-button-${title}`}
            onClick={() => navigateMonth('next')}
            className="nav-button"
          >
            {Arrow('right')}
          </button>
        </div>

        <div className="grid grid-cols-7">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="text-center mb-2.5">
              <span className="day-label">{day}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {days.map((date, index) => {
            if (!date) {
              return <div key={index} className="w-9 h-9"></div>;
            }

            const isCurrentDay = isToday(date);
            const isStart = isStartDate(date);
            const isEnd = isEndDate(date);
            const inRange = isInRange(date);

            const getDayClasses = () => {
              let classes = ['day-cell'];

              if (isCurrentDay && !isStart && !isEnd) {
                classes.push('day-today');
              }

              if (singleDate && isStart) {
                classes.push('day-single');
              } else if (isStart && isEnd) {
                classes.push('day-single');
              } else if (isStart) {
                classes.push('day-start');
              } else if (isEnd) {
                classes.push('day-end');
              } else if (inRange) {
                classes.push('day-in-range');
              }

              return classes.join(' ');
            };

            return (
              <div key={index} className="flex justify-center">
                <button
                  onClick={() => handleDateClick(date)}
                  className={getDayClasses()}
                >
                  {date.getDate()}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const hasSelection = selectedRange.startDate || selectedRange.endDate;
  const formatDate = (date: Date) =>
    date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  const displayText = (() => {
    if (singleDate) {
      return selectedRange.startDate ? formatDate(selectedRange.startDate) : placeholder;
    }

    if (selectedRange.startDate && selectedRange.endDate) {
      return `${formatDate(selectedRange.startDate)} - ${formatDate(selectedRange.endDate)}`;
    }

    if (selectedRange.startDate) {
      return formatDate(selectedRange.startDate);
    }

    return placeholder;
  })();

  return (
    <div ref={datePickerRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center justify-between w-full
          px-[13px] py-[9px]
          text-sm font-medium text-gray-800
          bg-white rounded-md
          hover:bg-gray-50 focus:outline-none
          ${
            isOpen
              ? 'focus:ring-2 focus:ring-purple-700 shadow-sm'
              : 'ring-1 ring-gray-300'
          }
        `}
      >
        <div className="w-full flex items-center justify-between gap-2">
          <span className="text-sm font-medium leading-5 text-gray-800">
            {displayText}
          </span>
        </div>
        <div
          className={`transition-transform duration-300 ${
            icon ? '' :
            isOpen ? 'rotate-180' : 'rotate-0'
            }
          }`}
        >
          {icon ?? Arrow('down')}
        </div>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 z-50 mt-1">
          <div
            className={`datepicker-container ${
              singleDate ? 'datepicker-container-single' : ''
            }`}
          >
            <div className="flex">
              {renderCalendarMonth(currentMonth, 'first')}
              {!singleDate && renderCalendarMonth(nextMonth, 'second')}
            </div>

            <div className="flex justify-start">
              <button
                onClick={clearSelection}
                disabled={!hasSelection}
                className={`clear-button ${
                  hasSelection
                    ? 'clear-button-enabled'
                    : 'clear-button-disabled'
                }`}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
